// Task list, task page and their writes (T20, T21). Lists are fresh for 30 s (plan §9) and keep
// the previous page on screen while the next one loads, so paging and filtering do not flash.
import {
  keepPreviousData,
  queryOptions,
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  createTasksApi,
  type EditOptions,
  type HistoryPage,
  type TaskDetail,
  type TaskListParams,
  type TaskPage,
  type UserTaskAction,
} from '@cis/api-client';
import { useMemo } from 'react';
import { peopleQueryOptions } from './planning.ts';

export const tasksApi = createTasksApi();

export const TASKS_KEY = ['tasks'] as const;

export const taskListQuery = (params: TaskListParams) =>
  queryOptions({
    queryKey: [...TASKS_KEY, 'list', params] as const,
    queryFn: ({ signal }) => tasksApi.list(params, signal),
  });

export function useTaskList(params: TaskListParams): UseQueryResult<TaskPage> {
  return useQuery({ ...taskListQuery(params), placeholderData: keepPreviousData });
}

const DETAIL_STALE_MS = 5_000; // plan §9: the open task is fresh for 5 s

export const taskDetailKey = (taskId: string, asOf: string | undefined) =>
  [...TASKS_KEY, 'detail', taskId, asOf ?? 'live'] as const;

export const taskDetailQuery = (taskId: string, asOf: string | undefined) =>
  queryOptions({
    queryKey: taskDetailKey(taskId, asOf),
    queryFn: ({ signal }) => tasksApi.get(taskId, { asOf, signal }),
    // A past day never changes; the live page is re-read after 5 s.
    staleTime: asOf === undefined ? DETAIL_STALE_MS : Infinity,
  });

export function useTaskDetail(
  taskId: string,
  asOf: string | undefined,
): UseQueryResult<TaskDetail> {
  return useQuery(taskDetailQuery(taskId, asOf));
}

/**
 * Display names by user id from GET /people (people.read), for history entries that store ids
 * (assigneeIds). Not read until `enabled`; an id it does not know stays an id.
 */
export function usePeopleNames(enabled: boolean): ReadonlyMap<number, string> {
  const people = useQuery({ ...peopleQueryOptions, enabled });
  return useMemo(
    () => new Map((people.data?.items ?? []).map((p) => [p.id, p.displayName] as const)),
    [people.data],
  );
}

export const taskHistoryKey = (taskId: string) => [...TASKS_KEY, 'history', taskId] as const;

export function useTaskHistory(taskId: string, page: number): UseQueryResult<HistoryPage> {
  return useQuery({
    queryKey: [...taskHistoryKey(taskId), page] as const,
    queryFn: ({ signal }) => tasksApi.history(taskId, { page, signal }),
    placeholderData: keepPreviousData,
  });
}

/** The fields of PATCH /tasks/:taskId that a write changes (M5-R3). */
export type TaskEdit = Omit<EditOptions, 'version'>;

export type TaskWrite =
  | { readonly kind: 'action'; readonly action: UserTaskAction; readonly text?: string }
  | { readonly kind: 'tick'; readonly stepNo: number; readonly ticked: boolean }
  // Re-plan (spec J3): who works the task or one step, and the re-planned dates.
  | { readonly kind: 'assign'; readonly userIds: readonly number[] }
  | { readonly kind: 'assign-step'; readonly stepNo: number; readonly userIds: readonly number[] }
  | {
      readonly kind: 'replan';
      readonly start?: string | null | undefined;
      readonly end?: string | null | undefined;
    }
  | { readonly kind: 'edit'; readonly edit: TaskEdit };

export interface TaskWriteInput {
  readonly write: TaskWrite;
  /** The task version the person saw (If-Match, constitution §4.7). */
  readonly version: number;
}

const taskWriteKey = (taskId: string) => [...TASKS_KEY, 'write', taskId] as const;

function sendWrite(taskId: string, { write, version }: TaskWriteInput): Promise<TaskDetail> {
  switch (write.kind) {
    case 'tick':
      return tasksApi.tick(taskId, { stepNo: write.stepNo, ticked: write.ticked, version });
    case 'assign':
      return tasksApi.assign(taskId, { userIds: write.userIds, version });
    case 'assign-step':
      return tasksApi.assignStep(taskId, write.stepNo, { userIds: write.userIds, version });
    case 'replan':
      return tasksApi.replan(taskId, { start: write.start, end: write.end, version });
    case 'edit':
      return tasksApi.edit(taskId, { ...write.edit, version });
    case 'action':
      return tasksApi.act(taskId, { action: write.action, text: write.text, version });
  }
}

/** Every task write answers with the task page, which replaces the cached live page. */
export function useTaskWrite(taskId: string): UseMutationResult<TaskDetail, Error, TaskWriteInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: taskWriteKey(taskId),
    mutationFn: (input) => sendWrite(taskId, input),
    onSuccess: (detail) => {
      queryClient.setQueryData(taskDetailKey(taskId, undefined), detail);
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [...TASKS_KEY, 'list'] }),
        queryClient.invalidateQueries({ queryKey: taskHistoryKey(taskId) }),
        // Assignments and dates move people's load and the Gantt bars (spec F9, F10).
        queryClient.invalidateQueries({ queryKey: ['people'] }),
        queryClient.invalidateQueries({ queryKey: ['gantt'] }),
      ]);
    },
  });
}

/** True while any write to this task is running: every write control is disabled (§5.5). */
export function useTaskWritePending(taskId: string): boolean {
  return useIsMutating({ mutationKey: taskWriteKey(taskId) }) > 0;
}
